import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Fingerprint } from "lucide-react";
import { ApiError } from "@/lib/api";
import { PasskeyError, supportsPasskeys } from "@/lib/webauthn";
import { Button, Card, ErrorNote, Field, Input } from "@/components/ui";
import { useAuth } from "@/app/auth";

export function SignInPage() {
  const { me, loading, signIn, signInWithPasskey } = useAuth();
  // Eine halb fertige Anmeldung gehört nicht hierher zurück: das Passwort war
  // richtig, es fehlt der zweite Faktor.
  const target = me?.mfaPending ? "/mfa" : "/";
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Der Passkey-Knopf: nur wenn der Browser es kann, und nur bis der Server
  // einmal gesagt hat, dass diese Installation keine anbietet.
  const [passkeys, setPasskeys] = useState(() => supportsPasskeys());
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  if (loading) return null;
  if (me) return <Navigate to={target} replace />;

  async function withPasskey() {
    setError(null);
    setPasskeyBusy(true);
    try {
      // Kein zweiter Faktor danach: der Passkey war Besitz und Entsperren
      // in einem, die Sitzung ist vollständig.
      await signInWithPasskey();
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.isPasskeysUnavailable) {
        setPasskeys(false);
        return;
      }
      if (err instanceof PasskeyError && err.cancelled) return;
      setError(
        err instanceof ApiError || err instanceof PasskeyError
          ? err.message
          : "Die Anmeldung mit Passkey ist fehlgeschlagen.",
      );
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const next = await signIn(email, password);
      navigate(next?.mfaPending ? "/mfa" : "/", { replace: true });
    } catch (err) {
      // The server deliberately does not say which of the two was wrong.
      setError(
        err instanceof ApiError
          ? err.detail
          : "Die Anmeldung ist fehlgeschlagen.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6 flex items-center gap-3">
          <span
            className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-600 text-sm font-bold text-white shadow-xs"
            aria-hidden
          >
            M
          </span>
          <div className="leading-tight">
            <h1 className="text-lg font-semibold tracking-tight">Mobilboxmanager</h1>
            <p className="text-sm muted">Verwaltung — bitte anmelden</p>
          </div>
        </div>

        <form className="flex flex-col gap-4" onSubmit={submit}>
          <Field label="E-Mail-Adresse">
            <Input
              type="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <Field label="Passwort">
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          {error && <ErrorNote>{error}</ErrorNote>}

          <Button type="submit" variant="primary" disabled={busy || passkeyBusy}>
            {busy ? "wird geprüft…" : "Anmelden"}
          </Button>
        </form>

        {passkeys && (
          <>
            <div
              className="my-4 flex items-center gap-3 text-xs muted"
              aria-hidden
            >
              <span className="h-px flex-1" style={{ background: "var(--border)" }} />
              oder
              <span className="h-px flex-1" style={{ background: "var(--border)" }} />
            </div>
            <Button
              type="button"
              className="w-full"
              disabled={busy || passkeyBusy}
              onClick={() => void withPasskey()}
            >
              <Fingerprint className="size-4" aria-hidden />
              {passkeyBusy ? "warte auf den Passkey…" : "Mit Passkey anmelden"}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
